import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  getMyTournaments, 
  saveTournamentImport, 
  getTournamentImports, 
  getTournamentImportResults 
} from '../../services/supabaseService';
import { useNotification } from '../../components/member/NotificationProvider';

export interface StandingRow {
  ranking: string | number;
  membershipNumber: string;
  userName: string;
  winPoints: string | number;
  omw: string;
  oomw: string;
  memo?: string;
  deckUrls?: string;
}

export interface DetectedTemplate {
  id: 'bandai_one_piece' | 'bandai_generic' | 'generic';
  name: string;
  gameId: number;
  gameName: string;
  icon: string;
  source: string;
  confidence: 'high' | 'medium';
  description: string;
}

export const detectTournamentCsvTemplate = (
  filename: string,
  headers: string[],
  rawContent: string
): DetectedTemplate => {
  const normHeaders = headers.map((h) => h.toLowerCase().trim());
  const normFilename = (filename || '').toLowerCase();
  const normContent = (rawContent || '').toLowerCase();

  const hasBandaiHeaders =
    normHeaders.some((h) => h.includes('membership')) &&
    normHeaders.some((h) => h.includes('win') || h.includes('point')) &&
    normHeaders.some((h) => h.includes('omw'));

  // Indicadores de One Piece no nome do arquivo ou conteúdo
  const isOnePieceFile =
    normFilename.includes('_op_') ||
    normFilename.includes('op_') ||
    normFilename.includes('-op-') ||
    normFilename.includes('one piece') ||
    normFilename.includes('one-piece') ||
    normFilename.includes('optcg') ||
    normFilename.includes('onepiece') ||
    normFilename.includes('matrona');

  const isOnePieceContent =
    normContent.includes('yamato') ||
    normContent.includes('luffy') ||
    normContent.includes('zoro') ||
    normContent.includes('nami') ||
    normContent.includes('kaido') ||
    normContent.includes('op0') ||
    normContent.includes('optcg');

  if (hasBandaiHeaders) {
    if (isOnePieceFile || isOnePieceContent) {
      return {
        id: 'bandai_one_piece',
        name: 'Bandai TCG+ • One Piece Card Game',
        gameId: 1, // One Piece no Cardumy
        gameName: 'One Piece Card Game',
        icon: 'fa-anchor',
        source: 'bandai_tcg_plus',
        confidence: 'high',
        description: 'Formato oficial Bandai TCG+ para One Piece Card Game (Ranking, Membership Number, Win Points, OMW%, OOMW%).'
      };
    }

    return {
      id: 'bandai_generic',
      name: 'Bandai TCG+ (Padrão Bandai)',
      gameId: 1,
      gameName: 'One Piece Card Game',
      icon: 'fa-gamepad',
      source: 'bandai_tcg_plus',
      confidence: 'medium',
      description: 'Formato padrão Bandai TCG+ de classificação.'
    };
  }

  return {
    id: 'generic',
    name: 'Classificação CSV',
    gameId: 1,
    gameName: 'One Piece Card Game',
    icon: 'fa-table-list',
    source: 'generic_csv',
    confidence: 'medium',
    description: 'Arquivo tabular de classificação.'
  };
};

export const parseStandingsCsv = (csvText: string): { headers: string[]; rows: StandingRow[] } => {
  const lines = csvText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (lines.length < 2) return { headers: [], rows: [] };

  const rawHeaders = lines[0].split(',').map((h) => h.trim().replace(/^["']|["']$/g, ''));
  const headers = rawHeaders.map((h) => h.toLowerCase());

  const rankIdx = headers.findIndex((h) => h.includes('ranking') || h === 'pos' || h === '#');
  const memberIdx = headers.findIndex((h) => h.includes('membership') || h.includes('bandai') || h.includes('number'));
  const userIdx = headers.findIndex((h) => h.includes('user') || h.includes('name') || h.includes('player') || h.includes('jogador'));
  const ptsIdx = headers.findIndex((h) => h.includes('win') || h.includes('point') || h.includes('pontos') || h.includes('pts'));
  const omwIdx = headers.findIndex((h) => h.includes('omw') && !h.includes('oomw'));
  const oomwIdx = headers.findIndex((h) => h.includes('oomw'));
  const memoIdx = headers.findIndex((h) => h.includes('memo'));
  const deckIdx = headers.findIndex((h) => h.includes('deck'));

  const rows: StandingRow[] = [];

  for (let i = 1; i < lines.length; i++) {
    const rawLine = lines[i];
    const cols = rawLine.split(',').map((c) => c.trim().replace(/^["']|["']$/g, ''));
    if (cols.length < 3) continue;

    const ranking = rankIdx !== -1 && cols[rankIdx] ? cols[rankIdx] : String(i);
    const membershipNumber = memberIdx !== -1 && cols[memberIdx] ? cols[memberIdx] : cols[1] || '-';
    const userName = userIdx !== -1 && cols[userIdx] ? cols[userIdx] : cols[2] || 'Jogador';
    const winPoints = ptsIdx !== -1 && cols[ptsIdx] ? cols[ptsIdx] : cols[3] || '0';
    const omw = omwIdx !== -1 && cols[omwIdx] ? cols[omwIdx] : cols[4] || '-';
    const oomw = oomwIdx !== -1 && cols[oomwIdx] ? cols[oomwIdx] : cols[5] || '-';
    const memo = memoIdx !== -1 && cols[memoIdx] ? cols[memoIdx] : undefined;
    const deckUrls = deckIdx !== -1 && cols[deckIdx] ? cols[deckIdx] : undefined;

    rows.push({
      ranking,
      membershipNumber,
      userName,
      winPoints,
      omw,
      oomw,
      memo,
      deckUrls
    });
  }

  return { headers: rawHeaders, rows };
};

export const ImportTournamentResults: React.FC = () => {
  const [searchParams] = useSearchParams();
  const preselectedId = searchParams.get('tournamentId');
  const { showNotification } = useNotification();

  const [tournaments, setTournaments] = useState<any[]>([]);
  const [savedImports, setSavedImports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Controle do Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTournamentId, setSelectedTournamentId] = useState<string>('');
  const [selectedGameId, setSelectedGameId] = useState<number>(1);

  // Dados do CSV em processamento / pré-visualização
  const [fileText, setFileText] = useState<string>('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [standings, setStandings] = useState<StandingRow[]>([]);
  const [detectedTemplate, setDetectedTemplate] = useState<DetectedTemplate | null>(null);
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);
  const [parsingError, setParsingError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Visualização de importação salva anteriormente
  const [viewingSavedImport, setViewingSavedImport] = useState<any | null>(null);
  const [loadingSavedDetails, setLoadingSavedDetails] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [tList, iList] = await Promise.all([
        getMyTournaments(),
        getTournamentImports()
      ]);
      const list = Array.isArray(tList) ? tList : [];
      setTournaments(list);
      setSavedImports(Array.isArray(iList) ? iList : []);

      if (preselectedId) {
        setSelectedTournamentId(String(preselectedId));
        setIsModalOpen(true);
      }
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenNewImportModal = (tournamentId?: string | number) => {
    setSelectedTournamentId(tournamentId ? String(tournamentId) : '');
    setSelectedGameId(1);
    setFileName(null);
    setFileText('');
    setStandings([]);
    setDetectedTemplate(null);
    setDuplicateWarning(null);
    setParsingError(null);
    setViewingSavedImport(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFileName(null);
    setFileText('');
    setStandings([]);
    setDetectedTemplate(null);
    setDuplicateWarning(null);
    setParsingError(null);
    setViewingSavedImport(null);
  };

  const computeSha256 = async (content: string): Promise<string> => {
    if (typeof crypto !== 'undefined' && crypto.subtle) {
      const msgBuffer = new TextEncoder().encode(content);
      const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
      return Array.from(new Uint8Array(hashBuffer))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    }
    return '';
  };

  const handleProcessCsv = async (csvContent: string, name: string) => {
    setParsingError(null);
    setDuplicateWarning(null);

    try {
      const { headers, rows } = parseStandingsCsv(csvContent);
      if (rows.length === 0) {
        setParsingError('O arquivo selecionado não contém linhas válidas de classificação.');
        setStandings([]);
        setFileName(null);
        setFileText('');
        setDetectedTemplate(null);
        return;
      }

      // Verificação de duplicidade de arquivo (por hash e nome)
      const computedHash = await computeSha256(csvContent);
      const isDuplicate = savedImports.find(
        (imp) => (computedHash && imp.file_hash === computedHash) || imp.filename === name
      );

      if (isDuplicate) {
        const importDate = new Date(isDuplicate.created_at).toLocaleDateString('pt-BR');
        const tourneyName = isDuplicate.tournament?.name 
          ? ` (vinculado ao torneio "${isDuplicate.tournament.name}")` 
          : '';
        setDuplicateWarning(
          `Este mesmo arquivo já foi importado no sistema em ${importDate} com o nome "${isDuplicate.filename}"${tourneyName}. Não é permitido importar o mesmo arquivo duas vezes.`
        );
      }

      // Reconhecimento automático do template (ex: One Piece Card Game / Bandai TCG+)
      const template = detectTournamentCsvTemplate(name, headers, csvContent);
      setDetectedTemplate(template);
      setSelectedGameId(template.gameId || 1);

      setFileName(name);
      setFileText(csvContent);
      setStandings(rows);
    } catch {
      setParsingError('Falha ao processar o arquivo CSV. Verifique a formatação do arquivo.');
      setStandings([]);
      setFileName(null);
      setFileText('');
      setDetectedTemplate(null);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      await handleProcessCsv(text, file.name);
    } catch {
      setParsingError('Erro ao ler o arquivo selecionado.');
    } finally {
      e.target.value = '';
    }
  };

  const handleSaveToDatabase = async () => {
    if (standings.length === 0 || !fileName || !fileText) {
      setParsingError('Nenhum arquivo CSV válido carregado para salvar.');
      return;
    }

    if (duplicateWarning) {
      showNotification('Este arquivo já foi importado anteriormente e não pode ser reenviado.', 'error');
      return;
    }

    setSaving(true);
    setParsingError(null);
    try {
      const res = await saveTournamentImport({
        tournamentId: selectedTournamentId ? Number(selectedTournamentId) : null,
        gameId: selectedGameId || detectedTemplate?.gameId || 1, // One Piece (game_id: 1)
        source: detectedTemplate?.source || 'bandai_tcg_plus',
        filename: fileName,
        fileText: fileText,
        rows: standings
      });

      if (res && res.success) {
        showNotification(
          `Classificação salva com sucesso no banco de dados! (${res.totalRows} jogadores registrados)`,
          'success'
        );
        handleCloseModal();
        const updatedImports = await getTournamentImports();
        setSavedImports(updatedImports || []);
      } else {
        const errMsg = res?.error || 'Erro ao salvar no banco de dados.';
        setParsingError(errMsg);
        showNotification(errMsg, 'error');
      }
    } catch (err: any) {
      const errMsg = err.message || 'Erro inesperado ao salvar importação.';
      setParsingError(errMsg);
      showNotification(errMsg, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Abrir modal para visualizar uma importação salva anteriormente
  const handleViewSavedImport = async (importItem: any) => {
    setLoadingSavedDetails(true);
    setViewingSavedImport(importItem);
    setSelectedTournamentId(importItem.tournament_id ? String(importItem.tournament_id) : '');
    setSelectedGameId(importItem.game_id || 1);
    setDuplicateWarning(null);
    setIsModalOpen(true);
    try {
      const results = await getTournamentImportResults(importItem.id);
      const mappedRows: StandingRow[] = (results || []).map((r: any) => ({
        ranking: r.ranking,
        membershipNumber: r.membership_number,
        userName: r.player_name,
        winPoints: r.win_points,
        omw: r.omw_pct !== null ? `${r.omw_pct}%` : '-',
        oomw: r.oomw_pct !== null ? `${r.oomw_pct}%` : '-',
        memo: r.memo,
        deckUrls: r.deck_urls
      }));
      setStandings(mappedRows);
      setFileName(importItem.filename);
      setDetectedTemplate({
        id: 'bandai_one_piece',
        name: importItem.game?.name ? `${importItem.game.name} (Bandai TCG+)` : 'One Piece Card Game (Bandai TCG+)',
        gameId: importItem.game_id || 1,
        gameName: importItem.game?.name || 'One Piece Card Game',
        icon: 'fa-anchor',
        source: importItem.source || 'bandai_tcg_plus',
        confidence: 'high',
        description: 'Classificação importada no banco de dados'
      });
    } catch (err) {
      console.error('Erro ao buscar resultados salvos:', err);
      showNotification('Não foi possível carregar os detalhes desta importação.', 'error');
    } finally {
      setLoadingSavedDetails(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto px-2 sm:px-4 pb-20">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
            <Link to="/meus-torneios" className="hover:text-purple-400 transition-colors">
              Meus Torneios
            </Link>
            <span>/</span>
            <span className="text-white">Classificação</span>
          </div>
          <h1 className="text-2xl font-black text-white uppercase tracking-tight">
            Importar Resultados de Torneio
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">
            Faça upload do arquivo CSV oficial (Bandai TCG+ • One Piece Card Game) e salve os resultados no banco de dados. O vínculo ao torneio pode ser feito agora ou depois.
          </p>
        </div>

        <button
          type="button"
          onClick={() => handleOpenNewImportModal()}
          className="px-5 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-black uppercase tracking-wider transition-colors flex items-center gap-2 self-start sm:self-auto cursor-pointer shadow-lg shadow-purple-600/25"
        >
          <i className="fas fa-file-import text-xs"></i>
          <span>Importar Novo CSV</span>
        </button>
      </div>

      {/* Histórico de Importações Salvas */}
      {savedImports.length > 0 && (
        <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-black uppercase tracking-wider text-slate-300 flex items-center gap-2">
              <i className="fas fa-clock-rotate-left text-purple-400"></i>
              <span>Classificações Registradas no Banco ({savedImports.length})</span>
            </h3>
          </div>

          <div className="space-y-2">
            {savedImports.map((imp) => (
              <div
                key={imp.id}
                onClick={() => handleViewSavedImport(imp)}
                className="p-3.5 rounded-2xl bg-slate-950 border border-slate-800/80 hover:border-purple-500/40 hover:bg-slate-900 transition-colors flex items-center justify-between gap-3 cursor-pointer group"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-xl bg-red-950/60 border border-red-500/20 text-red-400 flex items-center justify-center text-sm shrink-0">
                    <i className="fas fa-anchor"></i>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-bold text-white truncate group-hover:text-purple-300 transition-colors">
                        {imp.filename}
                      </p>
                      <span className="px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-[9px] font-bold shrink-0">
                        {imp.game?.name || 'One Piece'}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-400 mt-0.5">
                      <span className="text-emerald-400 font-semibold">{imp.total_rows} jogadores</span>
                      <span>•</span>
                      <span>
                        {imp.tournament?.name ? (
                          <span className="text-purple-300">Torneio: {imp.tournament.name}</span>
                        ) : (
                          <span className="text-slate-500 italic">Sem torneio vinculado</span>
                        )}
                      </span>
                      <span>•</span>
                      <span>{new Date(imp.created_at).toLocaleDateString('pt-BR')}</span>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewSavedImport(imp);
                  }}
                  className="px-3 py-1.5 rounded-lg bg-slate-800 group-hover:bg-purple-600 text-slate-200 group-hover:text-white text-xs font-bold transition-colors shrink-0"
                >
                  Ver Tabela
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lista de Torneios da Loja */}
      <div className="space-y-3">
        <h3 className="text-xs font-black uppercase tracking-wider text-slate-400 px-1">
          Torneios Cadastrados na Loja
        </h3>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
          </div>
        ) : tournaments.length === 0 ? (
          <div className="p-6 rounded-2xl bg-slate-900/40 border border-slate-800 text-center space-y-2">
            <p className="text-xs text-slate-400">
              Nenhum torneio cadastrado no momento. Você pode importar o arquivo CSV agora e associá-lo a um torneio depois!
            </p>
            <button
              type="button"
              onClick={() => handleOpenNewImportModal()}
              className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition-colors cursor-pointer"
            >
              Importar CSV Avulso
            </button>
          </div>
        ) : (
          tournaments.map((t) => (
            <div
              key={t.id}
              onClick={() => handleOpenNewImportModal(t.id)}
              className="p-4 sm:p-5 rounded-2xl bg-slate-900/60 border border-slate-800 hover:border-purple-500/50 hover:bg-slate-900 transition-colors flex items-center justify-between gap-4 cursor-pointer group"
            >
              <div className="flex items-center space-x-3.5">
                <div className="w-10 h-10 rounded-xl bg-purple-950/60 border border-purple-500/20 text-purple-400 flex items-center justify-center text-base shrink-0 group-hover:scale-105 transition-transform">
                  <i className="fas fa-trophy"></i>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white group-hover:text-purple-300 transition-colors">
                    {t.name}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400 mt-0.5">
                    <span className="text-red-400 font-semibold">{t.games?.name || t.cardgames?.name || 'One Piece'}</span>
                    <span>•</span>
                    <span>
                      {t.starts_at || t.start_date
                        ? new Date(t.starts_at || t.start_date).toLocaleDateString('pt-BR')
                        : 'Data a definir'}
                    </span>
                    <span>•</span>
                    <span className="capitalize">{t.status || 'Agendado'}</span>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenNewImportModal(t.id);
                }}
                className="px-4 py-2 rounded-xl bg-slate-800 group-hover:bg-purple-600 text-slate-200 group-hover:text-white text-xs font-bold transition-colors shrink-0 cursor-pointer"
              >
                Importar Resultados
              </button>
            </div>
          ))
        )}
      </div>

      {/* MODAL: Upload, Reconhecimento Automático e Tabela de Classificação */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-black/80">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl">
            {/* Cabeçalho do Modal */}
            <div className="p-5 border-b border-slate-800 bg-slate-950 flex items-start sm:items-center justify-between gap-4">
              <div>
                <span className="text-[10px] font-mono uppercase tracking-wider text-purple-400 font-bold block mb-0.5">
                  {viewingSavedImport ? 'Classificação Registrada' : 'Importação de Resultados'}
                </span>
                <h2 className="text-base sm:text-lg font-black text-white">
                  {fileName || 'Carregar Arquivo CSV de Classificação'}
                </h2>
                <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400 mt-1">
                  {!viewingSavedImport ? (
                    <div className="flex items-center gap-1.5">
                      <span>Associar a:</span>
                      <select
                        value={selectedTournamentId}
                        onChange={(e) => setSelectedTournamentId(e.target.value)}
                        className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-0.5 text-xs text-white focus:outline-none focus:border-purple-500 cursor-pointer"
                      >
                        <option value="">(Opcional) Nenhum - associar depois</option>
                        {tournaments.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <span>
                      {selectedTournamentId
                        ? `Vinculado ao torneio ID #${selectedTournamentId}`
                        : 'Não vinculado a nenhum torneio'}
                    </span>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={handleCloseModal}
                className="w-8 h-8 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-colors cursor-pointer text-sm shrink-0"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Conteúdo do Modal */}
            <div className="overflow-y-auto flex-1 p-4 sm:p-6 space-y-4">
              {loadingSavedDetails ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
                </div>
              ) : standings.length === 0 ? (
                /* CASO 1: CSV NÃO CARREGADO */
                <div className="space-y-4 py-8 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-purple-950/40 border border-purple-500/20 text-purple-400 flex items-center justify-center mx-auto text-2xl">
                    <i className="fas fa-file-csv"></i>
                  </div>

                  <div>
                    <h3 className="text-sm font-bold text-white">Carregar Arquivo CSV de Resultados</h3>
                    <p className="text-xs text-slate-400 mt-1 max-w-md mx-auto">
                      Selecione o arquivo CSV exportado do torneio (ex: Bandai TCG+ Standings do One Piece Card Game).
                    </p>
                  </div>

                  {parsingError && (
                    <div className="p-3 rounded-xl bg-rose-950/40 border border-rose-500/30 text-rose-400 text-xs max-w-md mx-auto">
                      {parsingError}
                    </div>
                  )}

                  <div className="pt-2">
                    <label className="px-6 py-3 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-black uppercase tracking-wider transition-colors cursor-pointer inline-flex items-center gap-2 shadow-lg shadow-purple-600/20">
                      <i className="fas fa-upload text-xs"></i>
                      <span>Selecionar Arquivo CSV</span>
                      <input
                        type="file"
                        accept=".csv,text/csv"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              ) : (
                /* CASO 2: CSV CARREGADO -> AVISO DE DUPLICIDADE (SE HOUVER) + TEMPLATE + TABELA */
                <div className="space-y-3">
                  {/* ALERTA DE ARQUIVO DUPLICADO */}
                  {duplicateWarning && (
                    <div className="p-4 rounded-2xl bg-amber-950/50 border border-amber-500/40 text-amber-300 text-xs flex items-start gap-3">
                      <div className="w-6 h-6 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-400 shrink-0 text-xs mt-0.5">
                        <i className="fas fa-triangle-exclamation"></i>
                      </div>
                      <div className="space-y-1">
                        <p className="font-bold text-white">Arquivo Já Importado</p>
                        <p className="text-amber-200/90 leading-relaxed">
                          {duplicateWarning}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Banner de Template Detectado Automaticamente */}
                  {detectedTemplate && (
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3.5 rounded-2xl bg-gradient-to-r from-red-950/40 via-slate-900 to-slate-950 border border-red-500/30">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-red-500/20 text-red-400 flex items-center justify-center text-base shrink-0">
                          <i className={`fas ${detectedTemplate.icon}`}></i>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] uppercase font-mono font-bold tracking-wider text-red-400">
                              Template Reconhecido
                            </span>
                            <span className="px-2 py-0.2 rounded-full bg-emerald-500/20 text-emerald-400 text-[9px] font-black uppercase tracking-wider">
                              Automático
                            </span>
                          </div>
                          <p className="text-xs font-bold text-white">
                            {detectedTemplate.name}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 shrink-0 self-end sm:self-auto text-xs">
                        <div className="flex items-center gap-1.5 bg-slate-950/80 px-2.5 py-1 rounded-lg border border-slate-800">
                          <span className="text-[10px] text-slate-500 font-mono">game_id:</span>
                          <span className="text-xs font-bold text-amber-400 font-mono">
                            {selectedGameId} (One Piece)
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between text-xs text-slate-400 px-1">
                    <span>
                      Total: <strong className="text-white font-mono">{standings.length}</strong> jogadores classificados
                    </span>
                    {!viewingSavedImport && (
                      <label className="text-purple-400 hover:text-purple-300 font-bold cursor-pointer text-xs flex items-center gap-1.5">
                        <i className="fas fa-arrows-rotate text-[10px]"></i>
                        <span>Trocar CSV</span>
                        <input
                          type="file"
                          accept=".csv,text/csv"
                          onChange={handleFileUpload}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>

                  {parsingError && (
                    <div className="p-3 rounded-xl bg-rose-950/40 border border-rose-500/30 text-rose-400 text-xs">
                      {parsingError}
                    </div>
                  )}

                  {/* Tabela de Classificação Limpa */}
                  <div className="overflow-x-auto rounded-2xl border border-slate-800">
                    <table className="w-full text-left text-xs text-slate-300">
                      <thead className="bg-slate-950 text-[10px] font-mono uppercase tracking-wider text-slate-400 border-b border-slate-800">
                        <tr>
                          <th className="py-2.5 px-3 text-center w-12">Pos.</th>
                          <th className="py-2.5 px-4">Nº Membro</th>
                          <th className="py-2.5 px-4">Jogador</th>
                          <th className="py-2.5 px-4 text-center">Pontos</th>
                          <th className="py-2.5 px-4 text-center">OMW %</th>
                          <th className="py-2.5 px-4 text-center">OOMW %</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800/60 bg-slate-900/40">
                        {standings.map((row, index) => {
                          const rankNum = Number(row.ranking) || index + 1;
                          return (
                            <tr
                              key={`${row.membershipNumber}-${index}`}
                              className={`hover:bg-slate-800/40 transition-colors ${
                                rankNum === 1 ? 'bg-amber-500/5 font-semibold' : ''
                              }`}
                            >
                              <td className="py-2 px-3 text-center font-bold">
                                {rankNum === 1 && <span className="text-amber-400">1º</span>}
                                {rankNum === 2 && <span className="text-slate-300">2º</span>}
                                {rankNum === 3 && <span className="text-amber-600">3º</span>}
                                {rankNum > 3 && <span className="text-slate-500">{rankNum}º</span>}
                              </td>
                              <td className="py-2 px-4 font-mono text-[11px] text-slate-400">
                                {row.membershipNumber}
                              </td>
                              <td className="py-2 px-4 font-bold text-white">
                                {row.userName}
                              </td>
                              <td className="py-2 px-4 text-center font-mono font-bold text-emerald-400">
                                {row.winPoints}
                              </td>
                              <td className="py-2 px-4 text-center font-mono text-slate-400">
                                {row.omw}
                              </td>
                              <td className="py-2 px-4 text-center font-mono text-slate-400">
                                {row.oomw}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Rodapé do Modal com Ação de Salvar no Banco */}
            <div className="p-4 border-t border-slate-800 bg-slate-950 flex items-center justify-between gap-3">
              <span className="text-xs text-slate-500 font-mono truncate max-w-xs sm:max-w-md">
                {standings.length > 0
                  ? `Campeão: ${standings[0]?.userName} (${standings[0]?.winPoints} pts)`
                  : 'Aguardando CSV'}
              </span>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold transition-colors cursor-pointer"
                >
                  Fechar
                </button>

                {!viewingSavedImport && standings.length > 0 && (
                  <button
                    type="button"
                    onClick={handleSaveToDatabase}
                    disabled={saving || !!duplicateWarning}
                    className={`px-5 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-colors flex items-center gap-1.5 cursor-pointer shadow-lg ${
                      duplicateWarning
                        ? 'bg-slate-800 text-slate-500 border border-slate-700 cursor-not-allowed shadow-none'
                        : 'bg-purple-600 hover:bg-purple-500 text-white shadow-purple-600/25'
                    }`}
                  >
                    {saving ? (
                      <>
                        <i className="fas fa-spinner fa-spin text-xs"></i>
                        <span>Salvando...</span>
                      </>
                    ) : duplicateWarning ? (
                      <>
                        <i className="fas fa-ban text-xs"></i>
                        <span>Já Importado</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-cloud-arrow-up text-xs"></i>
                        <span>Salvar no Banco</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
